// Queue-message shapes — the four SQS queues and the NotificationsQueue contract
// that crosses a repo boundary in BOTH directions. Transcribed from
// `contracts-legacy/notifications-backend/schemas.ts` (queue-message shapes of the
// exit gate). DonorReceiptMessage is NOT here: it belongs to §6.27, which the
// ratified §2 partition assigns to the payments contacts/bulk-action milestone.

import { z } from 'zod';

import {
  NOTIFICATION_ACTION_TYPES_THIS_REPO,
  NOTIFICATION_ENTITY_TYPES_THIS_REPO,
  NOTIFICATION_STATUSES,
  UUID_V4,
  presentKey,
  presentKeyMessage,
  pythonTruthy,
} from './shared.js';

/**
 * The job-trigger message (`schemas.ts:766`): written by all four start handlers
 * as exactly `json.dumps({'jobId': job_id})` — one key, nothing else.
 */
export const jobTriggerMessageSchema = z.object({ jobId: z.string().regex(UUID_V4) }).strict();

export type JobTriggerMessage = z.infer<typeof jobTriggerMessageSchema>;

/**
 * The job-RESUME message — a SECOND, DIFFERENT shape on the SAME two queues
 * (`schemas.ts:781-783`). The self-requeue path (`{jobId, resumeFrom}`) has NEVER
 * run in prod (dossier §7.X C7) — a consumer that assumes the one-key `{jobId}`
 * shape breaks on the first large job. `resumeFrom` is a batch START INDEX
 * (`range(start_offset, total, BATCH_SIZE)`), so a non-negative integer.
 */
export const jobResumeMessageSchema = z
  .object({ jobId: z.string().regex(UUID_V4), resumeFrom: z.number().int().nonnegative() })
  .strict();

export type JobResumeMessage = z.infer<typeof jobResumeMessageSchema>;

/** Either shape, as a consumer of TaxDocQueue / BulkActionQueue must accept. */
export const jobQueueMessageSchema = z.union([jobTriggerMessageSchema, jobResumeMessageSchema]);

export type JobQueueMessage = z.infer<typeof jobQueueMessageSchema>;

/** Retry semantics pinned in the legacy contract (`schemas.ts:751-760`). */
export const QUEUE_CONFIG = {
  ExportQueue: { visibilityTimeoutSec: 900, maxReceiveCount: 1, dlqRetentionDays: 7 },
  BulkActionQueue: { visibilityTimeoutSec: 900, maxReceiveCount: 1, dlqRetentionDays: 7 },
  TaxDocQueue: { visibilityTimeoutSec: 960, maxReceiveCount: 3, dlqRetentionDays: 7 },
  NotificationsQueue: {
    visibilityTimeoutSec: 60,
    maxReceiveCount: 3,
    dlqRetentionDays: 7,
  },
} as const;

const notificationMetadataCommon = {
  jobId: z.string(),
  error: z.string(),
} as const;

/** export/worker.py:send_notification (`schemas.ts:865-894`). */
export const exportNotificationMetadataSchema = z
  .object({
    ...notificationMetadataCommon,
    recordCount: z.number(),
    /** §6.25: the S3 artifact key `{orgId}/{userId}/{timestamp}-{label}[.csv|.zip]`. */
    s3Key: z.string(),
    /**
     * Read from `job.filters.transactionIDs`, NOT from `job.selectedIds` — the key
     * the start handler wrote; a rename that "corrects" this changes behaviour
     * (D-SELECTION-IDS-UNTYPED, `schemas.ts:870-890`). `z.unknown()`, NOT an array:
     * nothing on the path type-checks it, and the failure path does not sanitise.
     */
    selectedIds: z.unknown(),
    excludedIds: z.unknown(),
  })
  .strict()
  .refine(presentKey('selectedIds', 'export/worker.py:3472-3473'), {
    message: presentKeyMessage('selectedIds', 'export/worker.py:3472-3473'),
  })
  .refine(presentKey('excludedIds', 'export/worker.py:3472-3473'), {
    message: presentKeyMessage('excludedIds', 'export/worker.py:3472-3473'),
  });

export type ExportNotificationMetadata = z.infer<typeof exportNotificationMetadataSchema>;

/** bulk-action/worker.py:send_notification (`schemas.ts:897-922`). */
export const bulkActionNotificationMetadataSchema = z
  .object({
    ...notificationMetadataCommon,
    actionSubType: z.string(),
    successCount: z.number(),
    failedCount: z.number(),
    selectionMode: z.string(),
    /** D-SELECTION-IDS-UNTYPED (sibling hole, `schemas.ts:904-918`). */
    selectedIds: z.unknown(),
    excludedIds: z.unknown(),
  })
  .strict()
  .refine(presentKey('selectedIds', 'bulk-action/worker.py:997-998'), {
    message: presentKeyMessage('selectedIds', 'bulk-action/worker.py:997-998'),
  })
  .refine(presentKey('excludedIds', 'bulk-action/worker.py:997-998'), {
    message: presentKeyMessage('excludedIds', 'bulk-action/worker.py:997-998'),
  });

export type BulkActionNotificationMetadata = z.infer<typeof bulkActionNotificationMetadataSchema>;

/**
 * tax-documents/worker.py:send_notification — download jobs (`schemas.ts:955-963`).
 *
 * D-YEAR-UNVALIDATED: `year` is `z.unknown()`, NOT `string | number`. The only gate
 * on the value is a Python truthiness check at start; the worker emits it RAW (and a
 * DynamoDB `Decimal` round-trip can emit an integer year as a FLOAT — int in, float
 * out). Any JSON type survives; there is nothing narrower that is honest.
 */
export const taxDocumentsNotificationMetadataSchema = z
  .object({
    ...notificationMetadataCommon,
    recordCount: z.number(),
    s3Key: z.string(),
    year: z.unknown(),
    /** CON-857: no-donations contacts skipped (not failed); on every COMPLETED notification, 0 included. */
    skippedCount: z.number().int().nonnegative().optional(),
  })
  .strict()
  .refine(presentKey('year', 'tax-documents/worker.py:322'), {
    message: presentKeyMessage('year', 'tax-documents/worker.py:322'),
  });

export type TaxDocumentsNotificationMetadata = z.infer<
  typeof taxDocumentsNotificationMetadataSchema
>;

/**
 * tax-documents/worker.py:send_email_notification — email jobs (§6.18's owned
 * shape; `schemas.ts:974-1001`).
 *
 * THE THREE-VALUED OUTCOME VOCABULARY: `alreadySent` counts as success
 * (`emailed = delivered + alreadySent`), `skipped` is NEITHER success nor failure —
 * `skippedCount` is its own required field and must never be merged into either
 * neighbour. `emailedCount` / `queuedDraftCount` are added ONLY in draft_mode, are
 * jointly present or jointly absent, always carry the SAME value, and that value is
 * `successCount`. `queuedDraftCount` is a RETAINED ALIAS — consumers may already
 * read it; do not rename.
 */
export const emailTaxDocumentsNotificationMetadataSchema = z
  .object({
    ...notificationMetadataCommon,
    successCount: z.number(),
    failedCount: z.number(),
    skippedCount: z.number(),
    /** D-YEAR-UNVALIDATED — same raw passthrough as the download variant. */
    year: z.unknown(),
    emailedCount: z.number().optional(),
    queuedDraftCount: z.number().optional(),
  })
  .strict()
  .refine(presentKey('year', 'tax-documents/worker.py:562'), {
    message: presentKeyMessage('year', 'tax-documents/worker.py:562'),
  })
  .refine((m) => (m.emailedCount === undefined) === (m.queuedDraftCount === undefined), {
    message: 'emailedCount and queuedDraftCount are set together (draft_mode) or not at all',
  })
  .refine(
    (m) => m.emailedCount === undefined || m.emailedCount === m.queuedDraftCount,
    { message: 'queuedDraftCount is an alias of emailedCount and carries the same value' },
  )
  .refine(
    (m) => m.emailedCount === undefined || m.emailedCount === m.successCount,
    { message: 'in draft_mode both alias keys carry success_count' },
  );

export type EmailTaxDocumentsNotificationMetadata = z.infer<
  typeof emailTaxDocumentsNotificationMetadataSchema
>;

const notificationEnvelope = <A extends z.ZodTypeAny, E extends z.ZodTypeAny, M extends z.ZodTypeAny>(
  actionType: A,
  entityType: E,
  metadata: M,
) =>
  z
    .object({
      merchantName: z.string(),
      clerkUserID: z.string(),
      userEmail: z.string(),
      clerkOrganizationRole: z.string(),
      clerkOrganizationID: z.string(),
      actionType,
      entityType,
      status: z.enum(NOTIFICATION_STATUSES),
      actionLabel: z.string(),
      metadata,
    })
    .strict();

/** `schemas.ts:1023-1027`. */
export const exportNotificationMessageSchema = notificationEnvelope(
  z.literal('EXPORT'),
  z.string(),
  exportNotificationMetadataSchema,
);

export type ExportNotificationMessage = z.infer<typeof exportNotificationMessageSchema>;

/**
 * `schemas.ts:1029-1038`. The entityType is a closed 5-member set, chosen by a
 * nested conditional over the action type; `transactions` is the else-branch
 * default, so MARK_TAX_DEDUCTIBLE, RESEND_RECEIPTS and SOFT_DELETE all map to it.
 */
export const bulkActionNotificationMessageSchema = notificationEnvelope(
  z.literal('BULK_ACTION'),
  z.enum(['subscriptions', 'forms', 'elements', 'broadcasts', 'transactions']),
  bulkActionNotificationMetadataSchema,
);

export type BulkActionNotificationMessage = z.infer<typeof bulkActionNotificationMessageSchema>;

/** `schemas.ts:1040-1044`. */
export const taxDocumentsNotificationMessageSchema = notificationEnvelope(
  z.literal('TAX_DOCUMENTS'),
  z.literal('tax-documents'),
  taxDocumentsNotificationMetadataSchema,
);

export type TaxDocumentsNotificationMessage = z.infer<typeof taxDocumentsNotificationMessageSchema>;

/** `schemas.ts:1046-1050`. */
export const emailTaxDocumentsNotificationMessageSchema = notificationEnvelope(
  z.literal('EMAIL_TAX_DOCUMENTS'),
  z.literal('email-tax-documents'),
  emailTaxDocumentsNotificationMetadataSchema,
);

export type EmailTaxDocumentsNotificationMessage = z.infer<
  typeof emailTaxDocumentsNotificationMessageSchema
>;

/** Any message THIS repo's four producers can place on NotificationsQueue (`schemas.ts:1053-1058`). */
export const notificationMessageSchema = z.union([
  exportNotificationMessageSchema,
  bulkActionNotificationMessageSchema,
  taxDocumentsNotificationMessageSchema,
  emailTaxDocumentsNotificationMessageSchema,
]);

export type NotificationMessage = z.infer<typeof notificationMessageSchema>;

/**
 * What `notifications_writer` will ACCEPT — deliberately WIDER than what the repo
 * produces, and the ONE deliberately non-strict schema in the slice
 * (`schemas.ts:1060-1147`).
 *
 * WHY NOT `.strict()`: the writer reads exactly ten keys BY NAME and silently
 * DISCARDS everything else; an eleventh key from a cross-repo producer is accepted
 * and dropped. `.strict()` would turn the first marketing-side field addition into
 * a contract RED with no behaviour change — a false alarm on an open channel. The
 * folder rule bans `.passthrough()`; it is not needed — a plain Zod object already
 * accepts unknown keys and strips them from the parsed output, which models the
 * writer's discard exactly.
 *
 * WHY `z.unknown()` + pythonTruthy (D-CONSUMER-TRUTHINESS-NOT-TYPE): the writer
 * guards ONLY `clerkOrganizationID` and `actionType`, with a Python TRUTHINESS test,
 * not a type test. `7`, `["x"]`, `{"k": 1}` and `true` all pass; `""`, `0`, `[]`,
 * `{}`, `false` and `null` are skipped. The eight other fields are never inspected
 * at all. A missing key is genuinely accepted here (`.get(k, <default>)` reads), so
 * unlike the producer metadata schemas these are NOT `presentKey`-guarded.
 */
export const notificationMessageAsConsumedSchema = z.object({
  merchantName: z.unknown(),
  clerkUserID: z.unknown(),
  userEmail: z.unknown(),
  clerkOrganizationRole: z.unknown(),
  clerkOrganizationID: z
    .unknown()
    .refine(pythonTruthy, {
      message: 'clerkOrganizationID must be truthy in Python or the record is skipped',
    }),
  actionType: z
    .unknown()
    .refine(pythonTruthy, {
      message: 'actionType must be truthy in Python or the record is skipped',
    }),
  entityType: z.unknown(),
  status: z.unknown(),
  actionLabel: z.unknown(),
  metadata: z.unknown(),
});

export type NotificationMessageAsConsumed = z.infer<typeof notificationMessageAsConsumedSchema>;

/**
 * The in-repo producer literals, kept as a VALUE for consumers that enumerate them
 * (`schemas.ts:151-156`). NOT an input constraint — see
 * `notificationMessageAsConsumedSchema` for why the consumer stays open.
 */
export const NOTIFICATION_ACTION_TYPES = NOTIFICATION_ACTION_TYPES_THIS_REPO;

/**
 * Cross-repo residual (`schemas.ts:186-189`), recorded as a value so the open
 * channel is stated in code, not just prose.
 */
export const NOTIFICATION_ACTION_TYPES_RESIDUAL =
  'Producers outside this repo may send any actionType string. TWO are known, both reading the ' +
  'SSM-published queue URL: the marketing lambdalith (infra/marketing.ts:114) and the imports ' +
  "lambdalith (infra/imports.ts:108), the latter live with actionType 'CONTACT_IMPORT'.";

/** `entityType` values the four in-repo producers emit, as a VALUE (`schemas.ts:192-196`). */
export const NOTIFICATION_ENTITY_TYPES = NOTIFICATION_ENTITY_TYPES_THIS_REPO;
